/**
 * Status-badge styles tuned for the DealCloser Autopilot dark theme.
 *
 * Each variant is a semi-transparent alpha fill over a matching border,
 * readable on obsidian/surface backgrounds without the pastel-on-black
 * contrast problems that the stock Tailwind `bg-green-100 text-green-700`
 * pairings create on a dark surface.
 *
 * Usage: `<Badge className={STATUS_BADGE.active}>Active</Badge>`
 */
export const STATUS_BADGE = {
  // Positive / running / accepted
  success:
    'border-transparent bg-emerald-500/12 text-emerald-300 ring-1 ring-inset ring-emerald-500/25',
  // Neutral / draft / waiting
  neutral:
    'border-transparent bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)] ring-1 ring-inset ring-[rgba(255,255,255,0.08)]',
  // Soft hold / paused / scheduled
  warn:
    'border-transparent bg-amber-500/12 text-amber-300 ring-1 ring-inset ring-amber-500/25',
  // Brand-forward / completed / published / info
  info:
    'border-transparent bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] ring-1 ring-inset ring-[rgba(28,63,255,0.3)]',
  // Destructive / deleted / failed
  danger:
    'border-transparent bg-red-500/12 text-red-300 ring-1 ring-inset ring-red-500/25',
} as const

export type StatusBadgeVariant = keyof typeof STATUS_BADGE

/**
 * Canonical mapping for the 4-5 lead/campaign lifecycle statuses most of
 * the codebase uses. Pages that need custom labels can wrap these.
 */
export const CAMPAIGN_STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
  active: 'success',
  paused: 'warn',
  draft: 'neutral',
  completed: 'info',
  deleted: 'danger',
}

export const POST_STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
  draft: 'neutral',
  approved: 'info',
  scheduled: 'warn',
  published: 'success',
  failed: 'danger',
}
